import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { TenantSelector } from './TenantSelector';
import { ChatPanel } from './ChatPanel';
import clsx from 'clsx';

interface LayoutProps {
  isAdmin?: boolean;
}

export function Layout({ isAdmin = false }: LayoutProps) {
  const { user, logout, isStaff } = useAuthStore();
  const navigate = useNavigate();
  const [isChatOpen, setIsChatOpen] = useState(false);

  const clientNavItems = [
    { to: '/', label: 'Dashboard', icon: '📊' },
    { to: '/api-keys', label: 'API Keys', icon: '🔑' },
    { to: '/templates', label: 'Templates', icon: '📄' },
    { to: '/retailers', label: 'Retailers', icon: '🏪' },
    { to: '/test-label', label: 'Test Label', icon: '🏷️' },
    { to: '/usage', label: 'Usage', icon: '📈' },
    { to: '/docs', label: 'Documentation', icon: '📖' },
  ];

  const adminNavItems = [
    { to: '/admin', label: 'Dashboard', icon: '📊' },
    { to: '/admin/tenants', label: 'Tenants', icon: '👥' },
    { to: '/admin/retailers', label: 'Retailers', icon: '🏪' },
    { to: '/admin/onboarding', label: 'Onboarding', icon: '🚀' },
    { to: '/admin/templates', label: 'Templates', icon: '📄' },
    { to: '/admin/label-feedback', label: 'Label feedback', icon: '💬' },
    { to: '/admin/billing', label: 'Billing', icon: '💰' },
    { to: '/admin/monitoring', label: 'Monitoring', icon: '🔍' },
    { to: '/admin/renderer-compare', label: 'Renderer bake-off', icon: '🔬' },
    { to: '/admin/help', label: 'Help', icon: '❓' },
  ];

  const navItems = isAdmin ? adminNavItems : clientNavItems;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <span className="text-2xl">🏷️</span>
              <h1 className="text-xl font-bold text-gray-900">
                EDI Label Service
              </h1>
              {isAdmin && (
                <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded">
                  Admin
                </span>
              )}
              {!isAdmin && isStaff && (
                <TenantSelector />
              )}
            </div>
            
            <div className="flex items-center space-x-4">
              {isStaff && (
                <button
                  onClick={() => navigate(isAdmin ? '/' : '/admin')}
                  className="text-sm text-primary-600 hover:text-primary-800"
                >
                  {isAdmin ? 'Client View' : 'Admin View'}
                </button>
              )}
              <span className="text-sm text-gray-600">{user?.userDetails}</span>
              <button
                onClick={logout}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 min-h-[calc(100vh-4rem)] bg-white border-r">
          <nav className="p-4 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/' || item.to === '/admin'}
                className={({ isActive }: { isActive: boolean }) =>
                  clsx(
                    'flex items-center space-x-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  )
                }
              >
                <span>{item.icon}</span>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          <Outlet />
        </main>
      </div>

      {/* Chat FAB + Panel */}
      {!isChatOpen && (
        <button
          type="button"
          onClick={() => setIsChatOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-primary-600 text-white shadow-lg transition-transform hover:scale-105 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2"
          aria-label="Open chat assistant"
        >
          <span className="text-2xl" aria-hidden="true">💬</span>
        </button>
      )}

      <ChatPanel isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
    </div>
  );
}
